/**
 * Cadence — free-form interval + unit, with `'off'` as a first-class paused value.
 *
 * Used by:
 *   - `BrandAuditConfig.cadenceOverride[axis]` (per-brand × per-axis user setting)
 *   - `BrandSchedule.cadence` (cache of the value at last sync, on the brand row)
 *   - `EmailRecipient` (per-recipient digest cadence — added in S5)
 *
 * Design rationale: docs/scheduler-redesign-phases-s2-s6.md §0.2 D-2.
 */
export type Cadence =
  | { interval: number; unit: 'days' | 'weeks' | 'months' }
  | 'off';

/**
 * Audit types that can be scheduled (= every type with a cadence row in
 * `BrandAuditConfig.cadenceOverride`).
 *
 * `brand` (legacy combined type) and `social` are NOT included — `brand` is
 * read-only for historical audits and `social` is not yet implemented. Both
 * can be added later by extending this tuple + tier defaults below.
 */
// NOTE: `news_watch` is deliberately NOT here. "Studio de Création" is a PULL
// feature gated by the monthly `content_creations` quota (see metering.ts) — it
// has NO automatic cadence. It was removed from the schedulable set 2026-07-05;
// `syncBrand` prunes any pre-existing news_watch EventBridge schedule.
export const SCHEDULABLE_AUDIT_TYPES = [
  'website',
  'earned_media',
  'brand_narrative',
  'brand_authority',
  'brand_referral',
] as const;
export type SchedulableAuditType = (typeof SCHEDULABLE_AUDIT_TYPES)[number];

export function isSchedulableAuditType(t: string): t is SchedulableAuditType {
  return (SCHEDULABLE_AUDIT_TYPES as readonly string[]).includes(t);
}

/**
 * Tier defaults — applied when `brand.auditConfig.cadenceOverride[axis]` is unset.
 *
 * Numbers from docs/billing/pricing-and-billing.md §2.4 (the table is the source
 * of truth). Editing here without updating the doc is a bug.
 */
export const CADENCE_DEFAULTS: Record<string, Record<SchedulableAuditType, Cadence>> = {
  // website + earned_media are paid in CREDITS on every tier since pricing v2
  // (2026-11-04, `isCreditMetered`), so — like agency — no tier schedules them
  // by default: a default schedule would spend credits the customer didn't ask
  // for (Pro: weekly website 90 cr + EM 60 cr ≈ 600 cr/month vs 300 granted).
  // The user opts in per brand (isCadenceConfigurable). The single `website`
  // cadence drives the whole Phase-1 fan-out. brand_* axes stay daily.
  basic: {
    website: 'off',
    earned_media: 'off',
    brand_narrative: { interval: 1, unit: 'days' },
    brand_authority: { interval: 1, unit: 'days' },
    brand_referral: { interval: 1, unit: 'days' },
  },
  pro: {
    website: 'off',
    earned_media: 'off',
    brand_narrative: { interval: 1, unit: 'days' },
    brand_authority: { interval: 1, unit: 'days' },
    brand_referral: { interval: 1, unit: 'days' },
  },
  elite: {
    website: 'off',
    earned_media: 'off',
    brand_narrative: { interval: 1, unit: 'days' },
    brand_authority: { interval: 1, unit: 'days' },
    brand_referral: { interval: 1, unit: 'days' },
  },
  enterprise: {
    website: 'off',
    earned_media: 'off',
    brand_narrative: { interval: 1, unit: 'days' },
    brand_authority: { interval: 1, unit: 'days' },
    brand_referral: { interval: 1, unit: 'days' },
  },
  // Agency brands have NO default schedule — agencies drive runs manually
  // (run-now is agency-only) and configure cadence per brand×axis themselves
  // when they want automation. A default schedule would auto-spend agency
  // credits the agency didn't ask for. `'off'` means syncBrand creates no
  // EventBridge schedule; an agency can still opt in via PATCH audit-config.
  agency: {
    website: 'off',
    earned_media: 'off',
    brand_narrative: 'off',
    brand_authority: 'off',
    brand_referral: 'off',
  },
};

/**
 * Whether a tier may CONFIGURE cadence for the given axis (or any axis when omitted).
 *
 * Prompt Studio (lot G): basic/pro/elite/enterprise may configure the three BP axes
 * (brand_narrative, brand_authority, brand_referral) 1-7 days via the popup footer.
 * website / earned_media: every tier since pricing v2 (2026-11-04) — they are paid
 * in credits everywhere, so the customer chooses when they run.
 * Without an axis: the legacy answer (enterprise || agency), still read by the
 * Product measure cadence — keep it unchanged.
 */
export function isCadenceConfigurable(tier: string, axis?: SchedulableAuditType): boolean {
  if (axis === 'brand_narrative' || axis === 'brand_authority' || axis === 'brand_referral') {
    return true; // every tier configures BP axes in the Prompt Studio footer
  }
  if (axis === 'website' || axis === 'earned_media') return true; // credit-metered on every tier
  // No axis — today's answer (enterprise || agency): Product measure cadence.
  return tier === 'enterprise' || tier === 'agency';
}

/**
 * Minimum cadence allowed per tier. Protects Bedrock RPM by preventing
 * sub-day cadences until the agency tier ships.
 *
 * If we ever add `unit: 'hours'`, the agency floor would land here as
 * something like `{ interval: 1, unit: 'hours' }`.
 */
export const CADENCE_FLOOR: Record<string, Cadence> = {
  basic: { interval: 1, unit: 'days' },
  pro: { interval: 1, unit: 'days' },
  elite: { interval: 1, unit: 'days' },
  enterprise: { interval: 1, unit: 'days' },
  agency: { interval: 1, unit: 'days' },
};

/** Convert a Cadence to a human-readable French label (UI-facing). */
export function describeCadence(c: Cadence): string {
  if (c === 'off') return 'Désactivé';
  const { interval, unit } = c;
  const singular = { days: 'jour', weeks: 'semaine', months: 'mois' }[unit];
  const plural = { days: 'jours', weeks: 'semaines', months: 'mois' }[unit];
  if (interval === 1) {
    return unit === 'months' ? 'Tous les mois' : `Tous les ${singular}s`;
  }
  return `Tous les ${interval} ${plural}`;
}

/**
 * Resolve effective cadence: brand override → tier default.
 *
 * NEW rule for brand_* axes (00b DQ3, lot G): a non-agency tenant with a stored
 * 'off' override resolves to the tier default (read-time only, never written).
 * agency keeps 'off' as-is (« Manuel »). website/earned_media 'off' unchanged.
 */
export function resolveCadence(
  cadenceOverride: Partial<Record<SchedulableAuditType, Cadence>> | undefined,
  tier: string,
  axis: SchedulableAuditType,
): Cadence {
  if (cadenceOverride && axis in cadenceOverride) {
    const stored = cadenceOverride[axis] as Cadence;
    // Non-agency stored 'off' on a brand_* axis → resolve to the tier default
    if (stored === 'off' && tier !== 'agency' && BRAND_PROMPT_AXES.has(axis)) {
      return CADENCE_DEFAULTS[tier]?.[axis] ?? { interval: 1, unit: 'days' };
    }
    return stored;
  }
  return CADENCE_DEFAULTS[tier]?.[axis] ?? 'off';
}

/**
 * Measurement cadence (Product module "Visibilité GEO") default: not a
 * `SchedulableAuditType` (it never goes through the Trigger Lambda — the
 * schedule invokes `MeasureRunService.start` directly), so it has its own
 * tier default, NOT a lookup into `CADENCE_DEFAULTS`. Non-agency runs daily
 * (paid for via the shared `prompts` gauge already); agency defaults to
 * `'off'` (on-demand) and may opt into a cadence per audience.
 */
export function resolveMeasureCadence(tier: string, audience: { measureCadence?: Cadence }): Cadence {
  if (audience.measureCadence) return audience.measureCadence;
  return tier === 'agency' ? 'off' : { interval: 1, unit: 'days' };
}

/** Convert a cadence to an approximate day count (months = 31 days for W computation, 00b DQ5). */
export function cadenceToDays(c: Exclude<Cadence, 'off'>): number {
  const mult = c.unit === 'days' ? 1 : c.unit === 'weeks' ? 7 : 31;
  return c.interval * mult;
}

/** Cadence period in milliseconds (matches the `rate(N days)` expression). */
export function cadenceIntervalMs(c: Exclude<Cadence, 'off'>): number {
  return cadenceToDays(c) * 24 * 60 * 60 * 1000;
}

/**
 * Next scheduled fire for a materialized schedule, computed locally with NO
 * AWS call — so it scales to any number of brands/axes for free.
 *
 * EventBridge `rate(N days)` with a `StartDate` fires at `firstRunAt`, then
 * every interval after. The next fire is the first such point strictly after
 * `now`. Returns null when the schedule is paused/absent.
 *
 * @param firstRunAt ISO timestamp of the first fire (BrandSchedule.firstRunAt);
 *                   falls back to `createdAt` for legacy schedules written
 *                   before anchoring shipped.
 */
export function nextRunAt(
  cadence: Cadence,
  firstRunAt: string | undefined,
  now: Date = new Date(),
): Date | null {
  if (cadence === 'off' || !firstRunAt) return null;
  const first = new Date(firstRunAt).getTime();
  if (Number.isNaN(first)) return null;
  const periodMs = cadenceIntervalMs(cadence);
  const nowMs = now.getTime();
  if (first > nowMs) return new Date(first); // first fire still pending
  const elapsed = nowMs - first;
  const periodsAhead = Math.floor(elapsed / periodMs) + 1;
  return new Date(first + periodsAhead * periodMs);
}

/** U3 (C15): a brand conversation cadence wins when set, except a stored 'off' off agency. */
function brandConversationCadenceWins(tier: string, brandCadence: Cadence | undefined): brandCadence is Cadence {
  return brandCadence !== undefined && (brandCadence !== 'off' || tier === 'agency');
}

/**
 * Effective conversation cadence (lot I2): brand cadence → legacy persona cadence → tier default.
 *
 * - The brand cadence (`auditConfig.conversationCadence`) wins when set. A brand `'off'` is agency
 *   « Manuel » only (U3, C15): on any other tier a stored one (left by a downgrade out of agency)
 *   reads as the tier default below, never as a persona cadence; read time, no write (C0).
 * - No brand cadence → the legacy per-persona value (`'off'` stays manual, #50).
 * - Tier default: pro weekly, elite / enterprise daily, basic daily
 *   (`CADENCE_DEFAULTS.basic.brand_narrative`), agency and unknown tiers `'off'`.
 */
export function resolveConversationCadence(
  tier: string,
  persona: { cadence?: Cadence },
  brandCadence?: Cadence,
): Cadence {
  if (brandConversationCadenceWins(tier, brandCadence)) return brandCadence;
  if (!brandCadence && persona.cadence) return persona.cadence;
  if (tier === 'pro') return { interval: 1, unit: 'weeks' };
  if (tier === 'elite' || tier === 'enterprise') return { interval: 1, unit: 'days' };
  return CADENCE_DEFAULTS[tier]?.brand_narrative ?? 'off';
}

/**
 * The anchor day of a `months` conversation cadence (00b DQ3), from the SAME source the cadence
 * came from in `resolveConversationCadence`: the brand's `conversationMonthAnchorDay` when the
 * brand cadence wins, else the persona's own `monthAnchorDay`. `undefined` when the resolved
 * cadence is not `months` (a tier default never is) or no anchor is stored — then `addCadence`
 * steps from the given day.
 */
export function resolveConversationMonthAnchor(
  tier: string,
  persona: { cadence?: Cadence; monthAnchorDay?: number },
  brand: { conversationCadence?: Cadence; conversationMonthAnchorDay?: number },
): number | undefined {
  const cadence = resolveConversationCadence(tier, persona, brand.conversationCadence);
  if (cadence === 'off' || cadence.unit !== 'months') return undefined;
  if (brandConversationCadenceWins(tier, brand.conversationCadence)) return brand.conversationMonthAnchorDay;
  return persona.monthAnchorDay;
}

/**
 * The SLOWEST cadence allowed per tier for BP axes (null = no ceiling, agency).
 * Only enforced when `axis` is a brand_* axis. (C-G4)
 */
export const CADENCE_CEILING: Record<string, Cadence | null> = {
  basic: { interval: 7, unit: 'days' },
  pro: { interval: 7, unit: 'days' },
  elite: { interval: 7, unit: 'days' },
  enterprise: { interval: 7, unit: 'days' },
  agency: null, // agency unrestricted; « Manuel » = 'off' stored
};

const BRAND_PROMPT_AXES: ReadonlySet<string> = new Set(['brand_narrative', 'brand_authority', 'brand_referral']);

/**
 * Throws when a cadence violates the tier floor or ceiling.
 * Error message ALWAYS starts with 'Cadence' (the moved handler relies on it).
 */
export function assertCadenceAllowed(tier: string, cadence: Cadence, axis?: SchedulableAuditType): void {
  const isBrandAxis = axis != null && BRAND_PROMPT_AXES.has(axis);

  if (cadence === 'off') {
    // Only agency may store 'off' on a brand_* axis (C16 U3)
    if (isBrandAxis && tier !== 'agency') {
      throw new Error(`Cadence « Manuel » n'est disponible que pour l'agence sur les axes de prompts`);
    }
    return;
  }

  // Floor check (unchanged)
  const floor = CADENCE_FLOOR[tier];
  if (floor && floor !== 'off' && cadenceToDays(cadence) < cadenceToDays(floor)) {
    throw new Error(
      `Cadence ${describeCadence(cadence)} is faster than tier ${tier} floor ${describeCadence(floor)}`,
    );
  }

  // Ceiling check (brand_* axes only, lot G C-G4)
  if (isBrandAxis) {
    const ceiling = CADENCE_CEILING[tier];
    if (ceiling != null && ceiling !== 'off' && cadenceToDays(cadence) > cadenceToDays(ceiling)) {
      throw new Error(
        `Cadence ${describeCadence(cadence)} is slower than tier ${tier} ceiling ${describeCadence(ceiling)}`,
      );
    }
  }
}
